import { useCallback, useRef } from 'react';
import { useAnalyticsContext } from 'src/common/metrics/AnalyticsContext';
export const useVideoAnalytics = ({ contentId, videoRequestId, player }) => {
    const rumClient = useAnalyticsContext();
    // State tracking
    const lastEventTime = useRef(0);
    const isFullscreen = useRef(false);
    const playerMode = useRef('INLINE');
    const isPlaying = useRef(false);
    // Timer tracking
    const periodicTimer = useRef(null);
    const initialTimer = useRef(null);
    const hasInitialEventSent = useRef(false);
    // Calculate time since last event
    const calculateViewDuration = () => {
        const now = Date.now();
        const duration = Math.floor((now - lastEventTime.current) / 1000);
        lastEventTime.current = now;
        return duration.toString();
    };
    // Send analytics event
    const sendAnalyticsEvent = useCallback((eventName, isPeriodicEvent = false) => {
        if (!hasInitialEventSent.current && !isPeriodicEvent) {
            return;
        }
        const data = {
            'content-id': contentId,
            'view-duration': calculateViewDuration(),
            'player-mode': playerMode.current,
            player: player,
            'video-request-id': videoRequestId,
            'event-name': eventName,
        };
        rumClient.recordEvent('CREATOR_VIDEO_PAGE', data);
    }, [contentId, player, videoRequestId, rumClient]);
    // Clear all timers
    const clearTimers = useCallback(() => {
        if (initialTimer.current) {
            clearTimeout(initialTimer.current);
            initialTimer.current = null;
        }
        if (periodicTimer.current) {
            clearInterval(periodicTimer.current);
            periodicTimer.current = null;
        }
    }, []);
    // Start timers for RUM event logging
    const startTimers = useCallback(() => {
        clearTimers();
        // Send initial event after 2 seconds (only once)
        if (!hasInitialEventSent.current) {
            initialTimer.current = setTimeout(() => {
                hasInitialEventSent.current = true;
                if (isPlaying.current) {
                    sendAnalyticsEvent('NONE', true);
                }
            }, 2000);
        }
        // Initial event at ~2s; periodic events every 30s thereafter (first periodic at ~28s).
        periodicTimer.current = setInterval(() => {
            sendAnalyticsEvent('NONE', true);
        }, 30000);
    }, [sendAnalyticsEvent]);
    // Video event handlers
    const onPlay = useCallback(() => {
        isPlaying.current = true;
        sendAnalyticsEvent('PLAYING');
    }, [sendAnalyticsEvent]);
    const onPause = useCallback((event) => {
        const video = event.target;
        isPlaying.current = false;
        // Only record STOPPED if video is paused (not ended)
        if (video.currentTime > 0 && !video.ended) {
            sendAnalyticsEvent('STOPPED');
        }
    }, [sendAnalyticsEvent]);
    const onEnded = useCallback(() => {
        isPlaying.current = false;
        sendAnalyticsEvent('ENDED');
        clearTimers();
    }, [sendAnalyticsEvent]);
    const onVolumeChange = useCallback((event) => {
        const video = event.target;
        sendAnalyticsEvent(video.muted ? 'MUTED' : 'UNMUTED');
    }, [sendAnalyticsEvent]);
    const onFullscreenChange = useCallback(() => {
        const isCurrentlyFullscreen = !!document.fullscreenElement;
        if (isCurrentlyFullscreen !== isFullscreen.current) {
            isFullscreen.current = isCurrentlyFullscreen;
            playerMode.current = isCurrentlyFullscreen ? 'FULL_SCREEN' : 'INLINE';
            sendAnalyticsEvent('MODE_CHANGED');
        }
    }, [sendAnalyticsEvent]);
    // Attach event listeners to video
    const attachVideoListeners = useCallback((video) => {
        lastEventTime.current = Date.now();
        startTimers();
        video.addEventListener('play', onPlay);
        video.addEventListener('pause', onPause);
        video.addEventListener('ended', onEnded);
        video.addEventListener('volumechange', onVolumeChange);
        document.addEventListener('fullscreenchange', onFullscreenChange);
    }, [onPlay, onPause, onEnded, onVolumeChange, onFullscreenChange, startTimers]);
    // Remove event listeners from video
    const detachVideoListeners = useCallback((video) => {
        clearTimers();
        isPlaying.current = false;
        hasInitialEventSent.current = false;
        video.removeEventListener('play', onPlay);
        video.removeEventListener('pause', onPause);
        video.removeEventListener('ended', onEnded);
        video.removeEventListener('volumechange', onVolumeChange);
        document.removeEventListener('fullscreenchange', onFullscreenChange);
    }, [onPlay, onPause, onEnded, onVolumeChange, onFullscreenChange]);
    return {
        attachVideoListeners,
        detachVideoListeners,
        recordVideoEvent: sendAnalyticsEvent,
    };
};
